const API_URL = process.env.REACT_APP_API_URL || "";
const API_KEY = process.env.REACT_APP_API_KEY || "";
const WEBSITE_URL = process.env.REACT_APP_WEBSITE_URL || "";

// URL d'upload vidéo : peut être configurée explicitement ou déduite de API_URL
const UPLOAD_VIDEO_URL =
  process.env.REACT_APP_UPLOAD_VIDEO_URL ||
  (API_URL ? API_URL.replace(/api-dyn\.php$/i, "upload-video.php") : "");

type ApiAction =
  | "get"
  | "insert"
  | "update"
  | "delete"
  | "login"
  | "updateplayer"
  | "insertplayer"
  | "changepassword"
  | "adminresetpassword"
  | "generateinitlink"
  | "initpassword"
  | "updateplayerphoto"
  | "trackevent";

interface ApiRequestParams {
  table: string;
  action: ApiAction;
  data?: any;
  where?: string;
  fields?: string[];
  limit?: number;
  offset?: number;
  order_by?: string;
  count_only?: boolean;
  structure?: any;
}

export async function apiRequest<T = any>(params: ApiRequestParams): Promise<T> {
  const res = await fetch(API_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ api_key: API_KEY, ...params }),
  });

  console.log("API Request:", {
    url: API_URL,
    method: "POST",
    body: { api_key: API_KEY ? "***" : "", ...params },
  });

  const text = await res.text();
  console.log("API Raw Response:", text); // Log de la réponse brute

  let json: any = null;
  try {
    json = JSON.parse(text);
    console.log("API Response JSON:", json);
  } catch {
    console.error("Réponse non-JSON de l'API:", text);
    throw new Error("Réponse non-JSON de l'API: " + text.slice(0, 200));
  }

  const apiError = json?.error || (json?.success === false ? "Requête API en échec" : null);
  if (!res.ok || apiError) {
    console.error("API Error", {
      url: res.url,
      status: res.status,
      statusText: res.statusText,
      error: json?.error,
      details: json?.details,
      success: json?.success,
      payload: json,
    });
    const details = json?.details ? `: ${json.details}` : "";
    throw new Error(`${apiError || res.statusText}${details}`);
  }

  return json;
}

// ==== Méthodes volley ====
export const loginUser = (username: string, password: string) =>
  apiRequest({ table: "users", action: "login", data: { username, password } });

export const getData = (table: string, options: Partial<ApiRequestParams> = {}) =>
  apiRequest({ table, action: "get", ...options });

export const insertData = (table: string, data: any | any[]) =>
  apiRequest({ table, action: "insert", data });

export const updateData = (table: string, data: any, where: string) =>
  apiRequest({ table, action: "update", data, where });

export const deleteData = (table: string, where: string, cascade: boolean = false) =>
  apiRequest({
    table,
    action: "delete",
    where,
    ...(cascade && { cascade: true }), // Ajout de l'option cascade si nécessaire
  });

export const updatePlayer = async (data: any) => {
  return apiRequest({
    table: "users",
    action: "updateplayer",
    data,
  });
};

export const insertPlayer = async (data: any) => {
  return apiRequest({
    table: "users",
    action: "insertplayer",
    data,
  });
};

export const changePassword = (username: string, oldPassword: string, newPassword: string) =>
  apiRequest({
    table: "users",
    action: "changepassword",
    data: { username, oldPassword, newPassword },
  });

export const adminResetPassword = (userId: string | number, newPassword: string) =>
  apiRequest({
    table: "users",
    action: "adminresetpassword",
    data: { userId, newPassword },
  });

export const generateInitLink = (
  userId: string | number,
  coachUsername: string,
  coachPassword: string
) =>
  apiRequest({
    table: "users",
    action: "generateinitlink",
    data: { userId, coachUsername, coachPassword },
  });

export const initPasswordFromLink = (params: {
  userId: string | number;
  exp: string | number;
  sig: string;
  newPassword: string;
}) =>
  apiRequest({
    table: "users",
    action: "initpassword",
    data: params,
  });

export type ActivityEventPayload = {
  eventType: string;
  userId?: string | number | null;
  username?: string | null;
  role?: string | null;
  tableName?: string | null;
  recordId?: string | number | null;
  schemaUid?: string | null;
  schemaName?: string | null;
  scope?: string | null;
  origin?: string | null;
  success?: boolean | null;
  durationMs?: number | null;
  metadata?: any;
};

export const trackActivityEvent = (payload: ActivityEventPayload) =>
  apiRequest({
    table: "activity_events",
    action: "trackevent",
    data: payload,
  });

/**
 * Met à jour la photo d'un joueur.
 * @param playerId - ID du joueur.
 * @param photo - Données de la photo en base64.
 */
export const updatePlayerPhoto = async (playerId: string, photo: string) => {
  return apiRequest({
    action: "updateplayerphoto",
    table: "users",
    data: { playerId, photo }, // Les données sont envoyées sous la clé `data`
  });
};

/**
 * Upload d'un fichier vidéo vers le serveur.
 * Retourne la réponse JSON brute du script d'upload (doit contenir au moins `url`).
 */
export const uploadVideoFile = async (file: File): Promise<any> => {
  const url = UPLOAD_VIDEO_URL;
  if (!url) {
    throw new Error("URL d'upload vidéo non configurée.");
  }

  const formData = new FormData();
  formData.append("video", file);
  formData.append("api_key", API_KEY);

  const res = await fetch(url, {
    method: "POST",
    body: formData,
  });

  const text = await res.text();

  let json: any = null;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error("Réponse non-JSON de l'API d'upload vidéo: " + text.slice(0, 200));
  }

  if (!res.ok || json.error || json.success === false) {
    throw new Error(json.error || res.statusText || "Erreur lors de l'upload de la vidéo.");
  }

  return json;
};

/**
 * Résout une URL de média en ajoutant le domaine si nécessaire.
 * - URL absolue (http/https) → renvoyée telle quelle.
 * - URL commençant par / (ex: /public/uploads/...) → préfixée par WEBSITE_URL ou l'origin de API_URL.
 */
export const resolveMediaUrl = (path: string | null | undefined): string => {
  if (!path) return "";
  const url = String(path);
  if (/^https?:\/\//i.test(url)) return url;
  if (!url.startsWith("/")) return url;

  const base = WEBSITE_URL || (API_URL ? new URL(API_URL).origin : "");
  if (!base) return url;
  return base.replace(/\/+$/, "") + "/public" + url;
};

// ==== Schémas (Volley Schema Studio) ====
type SchemasScope = string | number;

export type SchemaTactic = {
  id: number;
  name: string;
};

const toMysqlDateTime = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
};

const sqlEscape = (v: unknown) => String(v ?? "").replace(/'/g, "''");

const sanitizeScope = (scope: SchemasScope) => {
  const raw = String(scope);
  // Scope utilisable dans une clause WHERE côté API (évite les quotes / injections)
  return raw.replace(/[^a-zA-Z0-9:_-]/g, "").slice(0, 64) || "global";
};

export async function fetchVolleySchemas(scope: SchemasScope): Promise<any[] | null> {
  const safeScope = sanitizeScope(scope);

  // Si le scope est une équipe, on interroge aussi 'global' pour récupérer
  // les schémas sauvegardés sans équipe sélectionnée (scope fallback).
  // ORDER BY updated_at DESC + uniqById garantit de garder la version la plus récente.
  const whereClause =
    safeScope !== "global"
      ? `(scope='${sqlEscape(safeScope)}' OR scope='global')`
      : `scope='global'`;

  const res: any = await getData("schemas", {
    where: whereClause,
    order_by: "updated_at DESC",
    limit: 500,
  });

  const rows = Array.isArray(res?.data) ? res.data : [];
  if (rows.length === 0) return null;

  // Nouveau format : 1 ligne = 1 schéma (colonne schema_uid)
  const v2Rows = rows.filter((r: any) => r && typeof r === "object" && r.schema_uid);
  if (v2Rows.length > 0) {
    return v2Rows
      .map((r: any) => {
        const raw = r.data;
        if (!raw) return null;
        const attachMeta = (schema: any) => {
          if (!schema || typeof schema !== "object") return schema;
          if (schema.tacticId == null && r.tactic_id != null) schema.tacticId = Number(r.tactic_id);
          if (typeof schema.tactic !== "string" && typeof r.tactic === "string") schema.tactic = r.tactic;
          return schema;
        };

        if (typeof raw === "object") return attachMeta(raw);
        if (typeof raw === "string") {
          try {
            return attachMeta(JSON.parse(raw));
          } catch {
            return null;
          }
        }
        return null;
      })
      .filter(Boolean);
  }

  // Ancien format : 1 ligne contient un array JSON de schémas dans data
  const row = rows[0];
  const raw = row?.data;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}

export async function upsertVolleySchemas(scope: SchemasScope, schemas: any[]): Promise<void> {
  const safeScope = sanitizeScope(scope);
  const now = toMysqlDateTime(new Date());

  // V2 : 1 schéma = 1 ligne. On fait un "replace" best-effort : upsert toutes les lignes + supprime celles qui ont disparu.
  const safeUid = (uid: unknown) => String(uid ?? "").replace(/[^a-zA-Z0-9:_-]/g, "").slice(0, 64);
  const wanted = (Array.isArray(schemas) ? schemas : [])
    .map((s: any) => ({ ...s, id: String(s?.id ?? "") }))
    .filter((s: any) => !!safeUid(s.id));

  // On essaye de lister les rows existantes v2 (schema_uid). Si la colonne n'existe pas encore (ancien schéma), la requête peut échouer.
  let existingUids: string[] = [];
  try {
    const existing: any = await getData("schemas", {
      where: `scope='${sqlEscape(safeScope)}' AND schema_uid IS NOT NULL AND schema_uid <> ''`,
      fields: ["schema_uid"],
      limit: 1000,
    });
    existingUids = (Array.isArray(existing?.data) ? existing.data : [])
      .map((r: any) => safeUid(r?.schema_uid))
      .filter(Boolean);
  } catch {
    existingUids = [];
  }

  const wantedUids = new Set(wanted.map((s: any) => safeUid(s.id)));

  // Cache local (scope) pour éviter de re-query les tactiques en boucle
  const tacticCacheByName = new Map<string, number>();
  const getOrCreateTacticId = async (nameRaw: unknown): Promise<number | null> => {
    const name = String(nameRaw ?? "").trim();
    if (!name) return null;
    const key = name.toLowerCase();
    const cached = tacticCacheByName.get(key);
    if (cached != null) return cached;

    // Lookup
    try {
      const existing: any = await getData("schema_tactics", {
        where: `scope='${sqlEscape(safeScope)}' AND name='${sqlEscape(name)}'`,
        fields: ["id"],
        limit: 1,
        order_by: "updated_at DESC",
      });
      const row = Array.isArray(existing?.data) ? existing.data[0] : null;
      const idNum = row?.id != null ? Number(row.id) : NaN;
      if (Number.isFinite(idNum)) {
        tacticCacheByName.set(key, idNum);
        return idNum;
      }
    } catch {
      // ignore
    }

    // Create
    const inserted: any = await insertData("schema_tactics", {
      scope: safeScope,
      name,
      created_at: now,
      updated_at: now,
    });

    // api-dyn insert ne renvoie pas forcément l'id; on re-fetch.
    try {
      const created: any = await getData("schema_tactics", {
        where: `scope='${sqlEscape(safeScope)}' AND name='${sqlEscape(name)}'`,
        fields: ["id"],
        limit: 1,
        order_by: "updated_at DESC",
      });
      const row = Array.isArray(created?.data) ? created.data[0] : null;
      const idNum = row?.id != null ? Number(row.id) : NaN;
      if (Number.isFinite(idNum)) {
        tacticCacheByName.set(key, idNum);
        return idNum;
      }
    } catch {
      // ignore
    }

    // fallback: unknown id
    void inserted;
    return null;
  };

  // Upsert chaque schéma (SELECT id -> UPDATE by id, sinon INSERT)
  for (const s of wanted) {
    const uid = safeUid(s.id);

    const tacticId: number | null =
      s?.tacticId != null && Number.isFinite(Number(s.tacticId)) ? Number(s.tacticId) : await getOrCreateTacticId(s?.tactic);

    const payload = {
      scope: safeScope,
      schema_uid: uid,
      schema_name: String(s?.name ?? ""),
      schema_type: String(s?.type ?? ""),
      player_count: Number(s?.playerCount ?? 6),
      tactic_id: tacticId,
      tactic: String((s as any)?.tactic ?? ""),
      data: s,
      updated_at: now,
    };

    let existingId: number | null = null;
    try {
      const existing: any = await getData("schemas", {
        where: `scope='${sqlEscape(safeScope)}' AND schema_uid='${sqlEscape(uid)}'`,
        fields: ["id"],
        limit: 1,
        order_by: "updated_at DESC",
      });
      const row = Array.isArray(existing?.data) ? existing.data[0] : null;
      const idNum = row?.id != null ? Number(row.id) : NaN;
      existingId = Number.isFinite(idNum) ? idNum : null;
    } catch {
      existingId = null;
    }

    if (existingId != null) {
      await updateData("schemas", payload, `id = ${existingId}`);
    } else {
      await insertData("schemas", {
        ...payload,
        created_at: now,
      });
    }
  }

  // Supprimer les schémas v2 en DB qui ne sont plus dans la liste
  if (existingUids.length > 0) {
    for (const uid of existingUids) {
      if (!uid) continue;
      if (wantedUids.has(uid)) continue;
      try {
        await deleteData("schemas", `scope='${sqlEscape(safeScope)}' AND schema_uid='${sqlEscape(uid)}'`, false);
      } catch {
        // ignore
      }
    }
  }
}

// ==== Tactiques (CRUD) ====
export async function fetchSchemaTactics(scope: SchemasScope): Promise<SchemaTactic[]> {
  const safeScope = sanitizeScope(scope);
  const res: any = await getData("schema_tactics", {
    where: `scope='${sqlEscape(safeScope)}'`,
    order_by: "name ASC",
    limit: 500,
  });
  const rows = Array.isArray(res?.data) ? res.data : [];
  return rows
    .map((r: any) => ({ id: Number(r?.id), name: String(r?.name ?? "") }))
    .filter((t: SchemaTactic) => Number.isFinite(t.id) && !!t.name.trim());
}

export async function createSchemaTactic(scope: SchemasScope, name: string): Promise<void> {
  const safeScope = sanitizeScope(scope);
  const now = toMysqlDateTime(new Date());
  const clean = String(name ?? "").trim();
  if (!clean) return;
  await insertData("schema_tactics", { scope: safeScope, name: clean, created_at: now, updated_at: now });
}

export async function updateSchemaTactic(scope: SchemasScope, id: number, name: string): Promise<void> {
  const safeScope = sanitizeScope(scope);
  const now = toMysqlDateTime(new Date());
  const clean = String(name ?? "").trim();
  if (!Number.isFinite(Number(id))) return;
  if (!clean) return;
  await updateData("schema_tactics", { name: clean, updated_at: now }, `id = ${Number(id)} AND scope='${sqlEscape(safeScope)}'`);
}

export async function deleteSchemaTactic(scope: SchemasScope, id: number): Promise<void> {
  const safeScope = sanitizeScope(scope);
  if (!Number.isFinite(Number(id))) return;
  // Délier les schémas
  try {
    await updateData("schemas", { tactic_id: null }, `scope='${sqlEscape(safeScope)}' AND tactic_id = ${Number(id)}`);
  } catch {
    // ignore
  }
  await deleteData("schema_tactics", `id = ${Number(id)} AND scope='${sqlEscape(safeScope)}'`);
}

// Migration helper: si on a des schémas avec tactic (texte) mais pas tactic_id, on crée/associe les tactiques.
export async function migrateSchemaTacticsFromSchemas(scope: SchemasScope): Promise<void> {
  const safeScope = sanitizeScope(scope);
  const rows = (await fetchVolleySchemas(safeScope)) || [];
  if (!Array.isArray(rows) || rows.length === 0) return;
  // upsert réécrit tactic_id automatiquement
  await upsertVolleySchemas(safeScope, rows);
}

// ==== Réglages application (feature flags) ====
export async function getAppSetting(key: string): Promise<string | null> {
  const safeKey = String(key).replace(/[^a-zA-Z0-9_]/g, "");
  const res: any = await getData("app_settings", {
    where: `setting_key='${sqlEscape(safeKey)}'`,
    limit: 1,
  });
  const row = Array.isArray(res?.data) ? res.data[0] : null;
  return row?.setting_value ?? null;
}

export async function setAppSetting(key: string, value: string): Promise<void> {
  const safeKey = String(key).replace(/[^a-zA-Z0-9_]/g, "");
  const now = toMysqlDateTime(new Date());
  const existing: any = await getData("app_settings", {
    where: `setting_key='${sqlEscape(safeKey)}'`,
    fields: ["id"],
    limit: 1,
  });
  const row = Array.isArray(existing?.data) ? existing.data[0] : null;
  if (row?.id != null) {
    await updateData("app_settings", { setting_value: value, updated_at: now }, `id = ${Number(row.id)}`);
  } else {
    await insertData("app_settings", { setting_key: safeKey, setting_value: value, updated_at: now });
  }
}

// Activé par défaut tant qu'aucun superadmin n'a explicitement désactivé le quiz.
export const isQuizEnabled = async (): Promise<boolean> => {
  const v = await getAppSetting("quiz_enabled");
  return v === null ? true : v === "1";
};

export const setQuizEnabled = (enabled: boolean) => setAppSetting("quiz_enabled", enabled ? "1" : "0");

// Cartons : activés par défaut tant qu'un superadmin ne les a pas explicitement désactivés.
export const isCardsEnabled = async (): Promise<boolean> => {
  const v = await getAppSetting("cards_enabled");
  return v === null ? true : v === "1";
};

export const setCardsEnabled = (enabled: boolean) => setAppSetting("cards_enabled", enabled ? "1" : "0");

// Révision des schémas (page Studio pour les joueuses) : activée par défaut.
// Quand c'est désactivé, seuls les admins/coachs gardent l'accès au Studio.
export const isSchemasEnabled = async (): Promise<boolean> => {
  const v = await getAppSetting("schemas_enabled");
  return v === null ? true : v === "1";
};

export const setSchemasEnabled = (enabled: boolean) => setAppSetting("schemas_enabled", enabled ? "1" : "0");

// Migration helper : si on détecte l'ancien format (1 ligne = array), on le splitte en lignes v2.
export async function migrateVolleySchemasToRows(scope: SchemasScope): Promise<boolean> {
  const safeScope = sanitizeScope(scope);
  const now = toMysqlDateTime(new Date());

  const res: any = await getData("schemas", {
    where: `scope='${sqlEscape(safeScope)}'`,
    order_by: "updated_at DESC",
    limit: 50,
  });
  const rows = Array.isArray(res?.data) ? res.data : [];
  if (rows.length === 0) return false;

  // Si on a déjà des lignes v2, rien à faire.
  if (rows.some((r: any) => r && typeof r === "object" && r.schema_uid)) return false;

  const raw = rows[0]?.data;
  let list: any[] = [];
  if (Array.isArray(raw)) list = raw;
  else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) list = parsed;
    } catch {
      list = [];
    }
  }
  if (list.length === 0) return false;

  // Insert batch : 1 ligne par schéma.
  const safeUid = (uid: unknown) => String(uid ?? "").replace(/[^a-zA-Z0-9:_-]/g, "").slice(0, 64);
  const batch = list
    .map((s: any) => {
      const uid = safeUid(s?.id);
      if (!uid) return null;
      return {
        scope: safeScope,
        schema_uid: uid,
        schema_name: String(s?.name ?? ""),
        schema_type: String(s?.type ?? ""),
        player_count: Number(s?.playerCount ?? 6),
        tactic: String((s as any)?.tactic ?? ""),
        data: s,
        created_at: now,
        updated_at: now,
      };
    })
    .filter(Boolean);

  if (batch.length === 0) return false;
  await insertData("schemas", batch as any);

  // Optionnel : on ne supprime pas l'ancienne ligne automatiquement (sécurité).
  return true;
}

// =====================
// Cartons (discipline)
// =====================
// Table `player_cards` (auto-créée par l'API au premier insert) :
// user_id, card_type ('yellow'|'red'), reason, given_by, status ('active'|'cleared'),
// created_at, cleared_at. Règle : 2 jaunes actifs = rouge ; rouge = prochain entraînement interdit.

export type PlayerCardRow = {
  id: number;
  user_id: string;
  card_type: "yellow" | "red";
  reason: string;
  given_by: string;
  status: "active" | "cleared";
  created_at: string;
  cleared_at?: string | null;
};

export type CardStatus = {
  yellows: number;
  reds: number;
  effective: "none" | "yellow" | "red";
  cards: PlayerCardRow[]; // cartons actifs uniquement
};

export function computeCardStatus(cards: PlayerCardRow[]): CardStatus {
  const active = cards.filter((c) => c.status === "active");
  const yellows = active.filter((c) => c.card_type === "yellow").length;
  const reds = active.filter((c) => c.card_type === "red").length;
  const effective = reds > 0 || yellows >= 2 ? "red" : yellows === 1 ? "yellow" : "none";
  return { yellows, reds, effective, cards: active };
}

export async function fetchActiveCards(userId?: string | number): Promise<PlayerCardRow[]> {
  const where =
    userId != null
      ? `status = 'active' AND user_id = '${sqlEscape(String(userId))}'`
      : `status = 'active'`;
  try {
    const res = await getData("player_cards", { where, order_by: "created_at ASC" });
    return Array.isArray(res?.data) ? res.data : [];
  } catch {
    return [];
  }
}

export async function giveCard(
  userId: string | number,
  cardType: "yellow" | "red",
  givenBy: string,
  reason = ""
): Promise<boolean> {
  try {
    const res = await insertData("player_cards", {
      user_id: String(userId),
      card_type: cardType,
      reason,
      given_by: givenBy,
      status: "active",
      created_at: toMysqlDateTime(new Date()),
    });
    return !!res?.success;
  } catch {
    return false;
  }
}

export async function clearCards(userId: string | number): Promise<boolean> {
  try {
    const res = await updateData(
      "player_cards",
      { status: "cleared", cleared_at: toMysqlDateTime(new Date()) },
      `user_id = '${sqlEscape(String(userId))}' AND status = 'active'`
    );
    return !!res?.success;
  } catch {
    return false;
  }
}
